import type { ReactNode } from 'react';
import Loading from '@standardsdigital/ui-components/Loading';
import Heading, { HeadingSize } from '@standardsdigital/ui-components/Heading';
import { useTranslation } from 'react-i18next';
import styles from './FormCard.module.css';
import FormCardFooter from './FormCardFooter/FormCardFooter';

interface FormCardProps {
  children: ReactNode;
  title: string;
  onSave?: () => void;
  onCancel?: () => void;
  disabled: boolean;
  loading: boolean;
  isEditMode: boolean;
  isDirty: boolean;
  dirtyFieldsCount?: number;
  hideActions?: boolean;
  hideContent?: boolean;
}

function FormCard({
  children,
  title,
  onSave,
  onCancel,
  disabled = false,
  loading = false,
  isEditMode = false,
  isDirty = false,
  dirtyFieldsCount = 0,
  hideActions = false,
  hideContent = false,
}: Readonly<FormCardProps>) {
  const { t } = useTranslation();

  const showFooter = !hideActions && Boolean(onSave || onCancel);
  const showChangesCounter = isEditMode && isDirty && dirtyFieldsCount > 0;
  const showCancelButton = Boolean(onCancel);
  const hasTitle = title.length > 0 && !hideContent;

  return (
    <section className={styles.card} aria-busy={loading}>
      {hasTitle ? (
        <Heading level={2} size={HeadingSize.Sm} className={styles.title}>
          {title}
        </Heading>
      ) : null}
      {!hideContent ? <div className={styles.body}>{children}</div> : null}
      {showFooter ? (
        <FormCardFooter
          onSave={onSave}
          onCancel={onCancel}
          disabled={disabled}
          loading={loading}
          showCancelButton={showCancelButton}
          showChangesCounter={showChangesCounter}
          dirtyFieldsCount={dirtyFieldsCount}
        />
      ) : null}
      <div
        className={`${styles.loadingOverlay} ${loading ? styles.loading : ''}`}
        role="status"
        aria-live="polite"
      >
        {loading ? <Loading aria-label={t('general.loading')} size="large" /> : null}
      </div>
    </section>
  );
}

export default FormCard;
